import { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getGenerationStatus } from '../../api/userApi';

const cyan = '#00e5ff';

const GenerationBonus = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(0);

  useEffect(() => {
    getGenerationStatus()
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load generation bonus data.'))
      .finally(() => setLoading(false));
  }, []);

  const slot = data?.slots?.[selectedSlot];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 48 }}>
      <Container className="pt-4" style={{ maxWidth: 680 }}>

        <button
          onClick={() => navigate('/dashboard')}
          style={{ background: 'none', border: 'none', color: cyan, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
        >
          ‹ Back to Dashboard
        </button>

        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 24 }}>Generation Bonus</h5>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: cyan }} />
          </div>
        ) : error ? (
          <div style={{ color: '#ff6666', textAlign: 'center', marginTop: 40 }}>{error}</div>
        ) : (
          <>
            {/* Total Earned */}
            <div style={{
              background: 'radial-gradient(ellipse at 0% 100%, rgba(102,255,153,0.1) 0%, #0d0d0d 60%)',
              border: '1px solid rgba(102,255,153,0.2)',
              borderRadius: 14,
              padding: '24px 28px',
              marginBottom: 28,
            }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                Total Generation Bonus Earned
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 42, fontWeight: 900, color: '#66ff99', textShadow: '0 0 24px rgba(102,255,153,0.3)' }}>
                  {data.totalEarned.toFixed(2)}
                </span>
                <span style={{ color: '#aaa', fontSize: 16, fontWeight: 500 }}>USDT</span>
              </div>
            </div>

            {/* Slot Tabs */}
            <div style={{
              display: 'flex',
              gap: 8,
              overflowX: 'auto',
              paddingBottom: 4,
              marginBottom: 20,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
            }}>
              {data.slots.map((s, idx) => (
                <button
                  key={s.planId}
                  onClick={() => setSelectedSlot(idx)}
                  style={{
                    flexShrink: 0,
                    padding: '6px 16px',
                    borderRadius: 20,
                    border: selectedSlot === idx
                      ? `1px solid ${cyan}`
                      : '1px solid rgba(255,255,255,0.1)',
                    background: selectedSlot === idx
                      ? 'rgba(0,229,255,0.12)'
                      : 'transparent',
                    color: selectedSlot === idx ? cyan : '#555',
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                  }}
                >
                  {s.planName}
                  {s.isSelfActive && (
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#66ff99',
                      position: 'absolute', top: 4, right: 4,
                      boxShadow: '0 0 4px rgba(102,255,153,0.6)',
                    }} />
                  )}
                </button>
              ))}
            </div>

            {/* Per-slot stats chips */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{
                background: 'rgba(0,229,255,0.07)',
                border: '1px solid rgba(0,229,255,0.15)',
                borderRadius: 8, padding: '10px 18px',
              }}>
                <div style={{ color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Active Directs
                </div>
                <div style={{ color: cyan, fontSize: 22, fontWeight: 800, marginTop: 2 }}>
                  {slot.directCount}
                </div>
                <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>with {slot.planName}</div>
              </div>
              <div style={{
                background: slot.isSelfActive ? 'rgba(102,255,153,0.07)' : 'rgba(255,170,68,0.07)',
                border: `1px solid ${slot.isSelfActive ? 'rgba(102,255,153,0.2)' : 'rgba(255,170,68,0.2)'}`,
                borderRadius: 8, padding: '10px 18px',
              }}>
                <div style={{ color: '#555', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Self Status
                </div>
                <div style={{ color: slot.isSelfActive ? '#66ff99' : '#ffaa44', fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                  {slot.isSelfActive ? '✓ Active' : '○ Inactive'}
                </div>
                <div style={{ color: '#444', fontSize: 10, marginTop: 2 }}>{slot.planName}</div>
              </div>
            </div>

            {/* Level Eligibility Table */}
            <div style={{ color: '#aaa', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Level Eligibility — {slot.planName}
            </div>
            <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(0,229,255,0.12)' }}>

              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '70px 80px 1fr 50px',
                background: 'rgba(0,229,255,0.07)',
                borderBottom: '1px solid rgba(0,229,255,0.15)',
                padding: '12px 20px',
              }}>
                {['Level', 'Bonus', 'Eligibility', 'Status'].map((h) => (
                  <div key={h} style={{ color: cyan, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {h}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {slot.levels.map((row, idx) => (
                <div
                  key={row.level}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '70px 80px 1fr 50px',
                    padding: '14px 20px',
                    alignItems: 'center',
                    borderBottom: idx < slot.levels.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    background: row.active ? 'rgba(102,255,153,0.04)' : 'transparent',
                    borderLeft: row.active ? '3px solid rgba(102,255,153,0.4)' : '3px solid transparent',
                  }}
                >
                  {/* Level circle */}
                  <div style={{
                    width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                    background: row.active ? 'rgba(102,255,153,0.15)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${row.active ? 'rgba(102,255,153,0.35)' : 'rgba(255,255,255,0.08)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 800,
                    color: row.active ? '#66ff99' : '#555',
                  }}>
                    {row.ordinal}
                  </div>

                  {/* Bonus */}
                  <div style={{ color: row.active ? '#66aaff' : '#444', fontWeight: 700, fontSize: 14 }}>
                    {row.bonus}
                  </div>

                  {/* Eligibility */}
                  <div>
                    <div style={{ color: row.active ? '#aaa' : '#444', fontSize: 12 }}>{row.eligibility}</div>
                    {row.level > 1 && (
                      <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>
                        {slot.directCount}/{row.required} active directs
                      </div>
                    )}
                  </div>

                  {/* Status symbol */}
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 20, color: row.active ? '#66ff99' : '#444', display: 'block', lineHeight: 1 }}>
                      {row.active ? '✓' : '○'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ color: '#444', fontSize: 11, textAlign: 'center', marginTop: 20 }}>
              Bonuses are credited to your Earning Wallet when eligible upline levels are unlocked.
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default GenerationBonus;
